import { GetAssetLocationsRequestDTO, GetLocationsRequestDTO, RecordState } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const locationKeys = {
  all: ['locations'] as const,
  list: (limit: number, isAWA: boolean, filters?: { state?: RecordState }) => [...locationKeys.all, 'list', limit, isAWA, filters] as const,
  infinite: (filters?: GetLocationsRequestDTO['filters'], orderBy?: GetLocationsRequestDTO['orderBy']) =>
    [...locationKeys.all, 'infinite', filters, orderBy] as const,
  detail: (id: string | undefined) => [...locationKeys.all, 'detail', id] as const,
};

export const assetLocationKeys = {
  all: ['asset-location'] as const,
  detail: (assetLocationId: string | undefined, isAWA: boolean) => [...assetLocationKeys.all, 'detail', assetLocationId, isAWA] as const,
};

export const locationLogKeys = {
  all: ['location-logs'] as const,
  infinite: (
    filters?: GetAssetLocationsRequestDTO['filters'],
    groupBy?: GetAssetLocationsRequestDTO['groupBy'],
    orderBy?: GetAssetLocationsRequestDTO['orderBy'],
  ) => [...locationLogKeys.all, 'infinite', filters, groupBy, orderBy] as const,
};

export const getLocationsQueryOptions = ({
  limit = 500,
  isAWA = false,
  filters,
}: {
  limit?: number;
  isAWA?: boolean;
  filters?: { state?: RecordState };
} = {}) => {
  return queryOptions({
    queryKey: locationKeys.list(limit, isAWA, filters),
    queryFn: async () => (await (isAWA ? AWAApiV2Service : ApiService).GetLocations({ limit, filters })).json(),
  });
};

export const getAssetLocationQueryOptions = ({ assetLocationId, isAWA = false }: { assetLocationId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: assetLocationKeys.detail(assetLocationId, isAWA),
    queryFn: async () => {
      const response = await (isAWA ? AWAApiV2Service : ApiService).GetLocations({
        limit: 1,
        filters: {
          state: RecordState.Active,
          fields: {
            id: [assetLocationId ?? ''],
          },
        },
      });
      return (await response.json()).data;
    },
    enabled: !!assetLocationId,
  });
};

export const getLocationsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetLocationsRequestDTO['filters'];
  orderBy?: GetLocationsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: locationKeys.infinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) => await (await ApiService.GetLocations({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getLocationLogsInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetAssetLocationsRequestDTO['filters'];
  groupBy?: GetAssetLocationsRequestDTO['groupBy'];
  orderBy?: GetAssetLocationsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: locationLogKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetAssetLocations({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getLocationByIdQueryOptions = ({ id }: { id: string | undefined }) => {
  return queryOptions({
    queryKey: locationKeys.detail(id),
    queryFn: async () => {
      if (!id) throw new Error('Location ID is required');
      return (await (await ApiService.GetLocationById({ locationId: id })).json()).location;
    },
    enabled: !!id,
  });
};
